import { addDays, differenceInCalendarDays, differenceInCalendarMonths, differenceInCalendarYears, format, getDay, parseISO, startOfWeek } from 'date-fns';
import Dosage from '@/fhirworks/types/Dosage';
import timezoneApi from '@/common/api/timezone.api';

// FHIR Timing.repeat.dayOfWeek codes, indexed by JS getDay().
const DAY_CODES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const DAY_UNITS = ['d', 'day', 'days'];
const WEEK_UNITS = ['wk', 'week', 'weeks'];
const MONTH_UNITS = ['mo', 'month', 'months'];
const YEAR_UNITS = ['a', 'yr', 'year', 'years'];

// Bounds the repeat.count walk so malformed data cannot spin forever.
const MAX_OCCURRENCE_SCAN_DAYS = 3650;

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Reduce an instant, a Date, or a bare day to the 'YYYY-MM-DD' calendar day it
 * falls on in `timeZone`. A bare day passes through untouched — it has no clock
 * to reinterpret, so converting it would spuriously shift it.
 *
 * @param {string|Date} date
 * @param {string} [timeZone] - IANA identifier; omitted falls back to the browser zone.
 * @returns {string|null}
 */
export const toDayKey = (date, timeZone) => {
    if (!date) return null;
    if (typeof date === 'string' && DAY_KEY.test(date)) return date;
    const instant = date instanceof Date ? date.toISOString() : date;
    try {
        return timezoneApi.getLocalDateTime(instant, 'yyyy-MM-dd', timeZone);
    } catch {
        return typeof instant === 'string' ? instant.slice(0, 10) : null;
    }
};

/**
 * Today as a facility-local 'YYYY-MM-DD'. Falls back to a UTC slice when the zone cannot be
 * resolved, which is the same contract toDayKey honours.
 *
 * @param {string} [timeZone] - IANA identifier
 * @returns {string}
 */
export const todayIn = (timeZone) => toDayKey(new Date().toISOString(), timeZone) || new Date().toISOString().slice(0, 10);

const segmentStart = (dosage) => toDayKey(dosage?.timing?.repeat?.boundsPeriod?.start);

const segmentEnd = (dosage) => toDayKey(dosage?.timing?.repeat?.boundsPeriod?.end);

const timesOf = (dosage) =>
    (dosage?.timing?.repeat?.timeOfDay || [])
        .filter(Boolean)
        .map((time) => String(time).slice(0, 5))
        .sort();

// FHIR `time` requires seconds; the pickers work in HH:mm. Storage is hh:mm:ss and the
// resolver reads back HH:mm, so Aidbox gets a valid time and the UI never sees the seconds.
const toFhirTime = (time) => {
    const text = String(time || '').trim();
    if (!/^\d{1,2}:\d{2}(:\d{2})?$/.test(text)) return null;
    const [hours, minutes, seconds = '00'] = text.split(':');
    return `${hours.padStart(2, '0')}:${minutes}:${seconds}`;
};

/**
 * The Quantity a dosage states, from either spelling of the choice type.
 *
 * @param {object} dosage
 * @returns {object|undefined}
 */
export const doseQuantityOf = (dosage) => {
    const entry = dosage?.doseAndRate?.[0];
    if (!entry) return undefined;
    return entry.dose?.Quantity ?? entry.doseQuantity;
};

/**
 * Is this dosage PRN? asNeededBoolean: false is an explicit "not PRN"; only absence or a
 * real value marks one.
 *
 * The single answer to what counts as PRN — every form, chip and label reads it from here,
 * so none of them can disagree with the resolver about whether a dose is expected.
 *
 * @param {object} dosage
 * @returns {boolean}
 */
export const isPrn = (dosage) => {
    const asNeeded = dosage?.asNeeded;
    if (!asNeeded) return false;
    return asNeeded.boolean !== false;
};

/**
 * Is this dosage a hold? Paired with `isPrn` — see its note on why both live here.
 *
 * @param {object} dosage
 * @returns {boolean}
 */
export const isHeld = (dosage) => Boolean(dosage?.hold);

const normalizeUnit = (unit) => String(unit || '').toLowerCase();

const intervalPeriod = (repeat) => {
    const period = Number(repeat?.period);
    return Number.isFinite(period) && period > 0 ? period : 1;
};

/**
 * The dose amounts the medication and taper forms offer. Quarters and halves because a
 * taper descends through them; whole numbers to ten because an order rarely states more.
 * Offered, not enforced — both fields are comboboxes.
 */
export const DOSE_AMOUNT_OPTIONS = ['0.25', '0.5', '1', '1.5', ...Array.from({ length: 9 }, (item, index) => String(index + 2))];

/**
 * The routes the medication forms offer, and the vocabulary doseFormOf reads a route into.
 * Offered, not enforced — the route fields are comboboxes.
 */
export const ROUTE_OPTIONS = [
    'Oral',
    'Sublingual',
    'Buccal',
    'Topical',
    'Transdermal',
    'Subcutaneous',
    'Intramuscular',
    'Intravenous',
    'Rectal',
    'Vaginal',
    'Nasal',
    'Inhalation',
    'Ophthalmic',
    'Otic',
];

/**
 * Does this segment's recurrence land on `day`?
 *
 * Both gates apply and both must pass: `dayOfWeek` filters which weekdays are
 * eligible, and `period`/`periodUnit` filters which repetition of the interval
 * `day` falls in. The interval is anchored to the segment's own
 * boundsPeriod.start — for a week unit, to the Monday of that start's week, so
 * "every 2 weeks on Mon/Wed/Fri" alternates whole weeks rather than sliding.
 * A unit finer than a day carries no calendar meaning against a wall-clock
 * timeOfDay list, so it filters nothing.
 */
const occursOn = (dosage, day) => {
    const start = segmentStart(dosage);
    if (!start || !day || day < start) return false;

    const repeat = dosage?.timing?.repeat;
    const dayOfWeek = repeat?.dayOfWeek || [];
    const dayDate = parseISO(day);
    if (dayOfWeek.length && !dayOfWeek.includes(DAY_CODES[getDay(dayDate)])) return false;

    const unit = normalizeUnit(repeat?.periodUnit);
    if (!unit) return true;

    const startDate = parseISO(start);
    const period = intervalPeriod(repeat);
    if (DAY_UNITS.includes(unit)) return differenceInCalendarDays(dayDate, startDate) % period === 0;
    if (WEEK_UNITS.includes(unit)) {
        const anchor = startOfWeek(startDate, { weekStartsOn: 1 });
        return Math.floor(differenceInCalendarDays(dayDate, anchor) / 7) % period === 0;
    }
    if (MONTH_UNITS.includes(unit)) {
        if (!dayOfWeek.length && dayDate.getDate() !== startDate.getDate()) return false;
        return differenceInCalendarMonths(dayDate, startDate) % period === 0;
    }
    if (YEAR_UNITS.includes(unit)) {
        if (!dayOfWeek.length && (dayDate.getDate() !== startDate.getDate() || dayDate.getMonth() !== startDate.getMonth())) return false;
        return differenceInCalendarYears(dayDate, startDate) % period === 0;
    }
    return true;
};

/**
 * The next `limit` days the segment's recurrence lands on, from `from` onward.
 * Bounded by the scan window so malformed data cannot spin forever, and stops at
 * boundsPeriod.end. Every caller that needs to walk the recurrence goes through
 * here, so a date this returns is a date the pass will produce.
 *
 * @param {object} dosage
 * @param {{from?: string, limit?: number}} [options] - `from` defaults to the segment's start.
 * @returns {string[]} 'YYYY-MM-DD' day keys, ascending
 */
const occurrenceDays = (dosage, { from, limit = 1 } = {}) => {
    const start = segmentStart(dosage);
    if (!start || limit < 1) return [];

    const end = segmentEnd(dosage);
    const days = [];
    let cursor = parseISO(from && from > start ? from : start);
    for (let scanned = 0; scanned < MAX_OCCURRENCE_SCAN_DAYS && days.length < limit; scanned++) {
        const day = format(cursor, 'yyyy-MM-dd');
        if (end && day > end) break;
        if (occursOn(dosage, day)) days.push(day);
        cursor = addDays(cursor, 1);
    }
    return days;
};

/**
 * The next days this segment expects doses on — what the schedule form previews so
 * the interval anchor is visible before saving. "Every 2 weeks on Wednesday" is
 * anchored to the Monday of the segment's own start week, which is not something a
 * clinician can read off the form fields.
 *
 * @param {object} dosage
 * @param {{from?: string, limit?: number}} [options]
 * @returns {string[]} 'YYYY-MM-DD' day keys, ascending
 */
export const nextOccurrences = (dosage, { from, limit = 3 } = {}) => occurrenceDays(dosage, { from, limit });

/**
 * How many administrations the segment produces per repetition of its interval.
 *
 * Not the number of times of day: a weekly interval contains one repetition per
 * selected weekday, so Mon/Wed/Fri at 08:00 is three per week, and twice each
 * Wednesday every two weeks is two per fortnight. This is the quantity an order's
 * `frequency` states, which is what makes the two comparable.
 *
 * @param {object} repeat - a Timing.repeat
 * @returns {number}
 */
export const administrationsPerInterval = (repeat) => {
    const times = (repeat?.timeOfDay || []).filter(Boolean).length;
    if (!times) return 0;

    const unit = normalizeUnit(repeat?.periodUnit);
    const days = (repeat?.dayOfWeek || []).filter(Boolean).length;
    // A day interval is one calendar day however the weekdays are filtered, so the
    // day list cannot multiply it.
    const perInterval = days && !DAY_UNITS.includes(unit) ? days : 1;
    return times * perInterval;
};

/**
 * How many times of day a schedule needs in order to reach `frequency` administrations per
 * interval, given the days it repeats on. The inverse of administrationsPerInterval, and
 * what the schedule forms open with — an order of twice daily wants two time slots, not one.
 *
 * @param {number} frequency - the order's administrations per interval
 * @param {object} [repeat] - a Timing.repeat, read for dayOfWeek and periodUnit
 * @returns {number} at least 1
 */
export const timesPerInterval = (frequency, repeat) => {
    const wanted = Number(frequency);
    if (!Number.isFinite(wanted) || wanted < 1) return 1;

    const unit = normalizeUnit(repeat?.periodUnit);
    const days = (repeat?.dayOfWeek || []).filter(Boolean).length;
    const perInterval = days && !DAY_UNITS.includes(unit) ? days : 1;
    return Math.max(1, Math.round(wanted / perInterval));
};

/**
 * The length of one repetition of the interval, in days. A unit finer than a day
 * carries no calendar meaning against a wall-clock timeOfDay list, so it measures one
 * day — the same reading occursOn gives it.
 */
export const intervalDays = (repeat) => {
    const period = intervalPeriod(repeat);
    const unit = normalizeUnit(repeat?.periodUnit);
    if (WEEK_UNITS.includes(unit)) return period * 7;
    if (MONTH_UNITS.includes(unit)) return period * 30;
    if (YEAR_UNITS.includes(unit)) return period * 365;
    return period;
};

/**
 * How much drug a segment gives per day: dose x administrations / interval length.
 *
 * The one number that compares two steps of a taper whatever they vary — dose, times
 * of day, days of week, or the interval itself. A step that halves the dose and
 * doubles the frequency is flat, which neither the dose nor the frequency alone says.
 *
 * @param {object} dosage - a Dosage, or Dosage-shaped JSON
 * @param {object} [fallbackDose] - a Quantity used when the segment states no dose of its own
 * @returns {number|null} null when there is no comparable dose or nothing is administered
 */
export const dosesPerDay = (dosage, fallbackDose) => {
    const dose = doseQuantityOf(dosage) ?? fallbackDose;
    const amount = Number(dose?.value);
    if (!Number.isFinite(amount)) return null;

    const perInterval = administrationsPerInterval(dosage?.timing?.repeat);
    if (!perInterval) return null;

    return (amount * perInterval) / intervalDays(dosage?.timing?.repeat);
};

/**
 * The day the segment's `count`th administration falls on, walking its own
 * recurrence. Returns null when the count is not exhausted inside the scan window.
 */
const countExhaustedOn = (dosage, count) => {
    const perOccurrence = timesOf(dosage).length || 1;
    const occurrences = Math.ceil(count / perOccurrence);
    if (occurrences < 1) return segmentStart(dosage);

    const days = occurrenceDays(dosage, { limit: occurrences });
    return days.length === occurrences ? days[days.length - 1] : null;
};

/**
 * Whether this segment is bounded by a duration of its own rather than by a date or a
 * count — a taper step's 'Continue for'.
 *
 * Deliberately NOT the test for "is this medication a taper": a final step running until
 * cancelled carries no duration, so a one-step taper answers false here. The chain-level
 * question is administrationTypeOf.
 *
 * @param {object} dosage
 * @returns {boolean}
 */
export const hasStepDuration = (dosage) => Boolean(dosage?.stepDuration);

/**
 * The schedule segments of a medication, in chain order.
 *
 * dosageInstruction[0] is the medication-level carrier — dose, route,
 * administration instructions, free-text sig — and carries no
 * boundsPeriod.start. It is never a segment: every medication already in the
 * database has one, so returning it would report the entire existing dataset as
 * scheduled.
 *
 * @param {object} med - a MedicationRequest
 * @returns {Array<object>} Dosage segments sorted by sequence ascending
 */
export const segmentChain = (med) => (med?.dosageInstruction || []).filter((dosage) => Boolean(segmentStart(dosage))).sort((a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0));

/**
 * The medication-level carrier entry — dose, route, administration instructions, free-text
 * sig — or undefined when there is none. It is the one entry with no boundsPeriod.start.
 *
 * @param {object} med
 * @returns {object|undefined}
 */
export const carrierOf = (med) => (med?.dosageInstruction || []).find((dosage) => !segmentStart(dosage));

/**
 * The prescriber's sig, as written, or '' when the medication states none.
 *
 * Two fields, because two writers. Fresh composes its own sig into timing.code.text, while
 * both ScriptSure ingest paths put the prescriber's into the carrier's text. Reading only the
 * first left every e-prescribed medication showing a blank Sig, in the very dialog whose job
 * is transcribing against it. Reading both also answers for the records already stored.
 *
 * @param {object} med
 * @returns {string}
 */
export const sigTextOf = (med) => {
    const carrier = carrierOf(med);
    return carrier?.timing?.code?.text || carrier?.text || '';
};

/**
 * Whether the medication is an e-Prescribe order rather than one recorded locally.
 *
 * @param {object} med
 * @returns {boolean}
 */
export const isPrescribedMedication = (med) => med?.intent === 'order';

/**
 * Who prescribed the medication, or '' when it names no one. A prescription owns its own
 * attribution, so an e-Prescribe order reports the prescriber ScriptSure sent back rather than
 * anything captured locally.
 *
 * @param {object} med
 * @returns {string}
 */
export const medicationPrescriberDisplay = (med) => {
    if (isPrescribedMedication(med)) return med.scriptSureWebhookLog?.at(-1)?.prescriber || '';
    return med?.requester?.display || '';
};

/**
 * @param {object} med
 * @returns {boolean}
 */
export const hasSchedule = (med) => segmentChain(med).length > 0;

const RXNORM_SYSTEM = 'http://www.nlm.nih.gov/research/umls/rxnorm';

const rxNormCodesOf = (med) => (med?.medication?.CodeableConcept?.coding || []).filter((coding) => coding?.system === RXNORM_SYSTEM && coding?.code).map((coding) => String(coding.code));

const drugNameOf = (med) => (med?.medication?.CodeableConcept?.text || '').trim().toLowerCase();

/**
 * Another medication the clinic is already giving for the same drug, or null.
 *
 * Matched on RxNorm where both sides carry it and on the name otherwise, because a medication
 * with no code still schedules end to end and is exactly the kind that gets typed in twice.
 *
 * This is the only moment a duplicate can turn into a double dose - not interaction checking,
 * which is out of scope, and not a reason to refuse: two entries for one drug are sometimes
 * right, and the nurse is the one who can tell.
 *
 * @param {object} med - the medication being added or scheduled
 * @param {Array<object>} medications - the patient's list
 * @param {{isInactive?: (med: object) => boolean}} [options]
 * @returns {object|null}
 */
export const administeredDuplicateOf = (med, medications, { isInactive = () => false } = {}) => {
    if (!med) return null;

    const codes = rxNormCodesOf(med);
    const name = drugNameOf(med);

    return (
        (medications || []).find((other) => {
            if (!other || other.id === med.id || isInactive(other)) return false;
            if (!hasSchedule(other)) return false;

            const otherCodes = rxNormCodesOf(other);
            if (codes.length && otherCodes.length) return otherCodes.some((code) => codes.includes(code));
            return Boolean(name) && drugNameOf(other) === name;
        }) || null
    );
};

/**
 * The colour an as-needed medication reads in, everywhere it is named.
 *
 * PRN is the one administration type with its own colour: it is the only one a nurse acts on
 * without a dose being due, so it has to be recognisable at a glance across the pass, the chart
 * and the prep list. Kept here rather than written at each chip, because five literals is how the
 * pass and the chart came to disagree in the first place.
 *
 * Purple, and its own theme colour rather than a borrowed one: an as-needed dose is acted on, so
 * it cannot read in the blue the eMAR uses for what is only being shown - a self-administered
 * medication is information, and this is work.
 *
 * The list's own PRN label is CSS and reads --v-theme-prn directly; keep the two in step.
 */
export const PRN_COLOR = 'prn';

/**
 * The theme colour for an administration type chip.
 *
 * @param {'scheduled'|'taper'|'prn'|null} type
 * @returns {string}
 */
export const administrationTypeColor = (type) => (type === 'prn' ? PRN_COLOR : 'primary');

/**
 * How this medication is administered: 'scheduled', 'taper' or 'prn'.
 *
 * The clinician's own choice when the record carries it. The fallback reads the chain, which
 * is what every medication written before the choice was stored has — and which cannot answer
 * for a one-step taper running until cancelled, since no step of it carries a duration.
 *
 * @param {object} med
 * @returns {'scheduled'|'taper'|'prn'|null} null when nothing has been chosen or scheduled
 */
export const administrationTypeOf = (med) => {
    if (med?.administrationType) return med.administrationType;

    const chain = segmentChain(med);
    if (!chain.length) return null;
    if (chain.some(hasStepDuration)) return 'taper';
    // A hold pauses the order; it does not restate it. Read the type through the hold, or a
    // held medication reads as Scheduled whatever it actually is.
    const ordered = chain.filter((entry) => !isHeld(entry));
    if (ordered.length && isPrn(ordered[ordered.length - 1])) return 'prn';
    return 'scheduled';
};

/**
 * Whether the medication's dose may still be corrected in place.
 *
 * This IS the dose lock in force today: MedicationsListWidget's `isDoseLocked` is its negation.
 * A dose with no schedule and no administration behind it stays correctable; anything else has
 * become a statement somebody may already have acted on.
 *
 * @param {object} med
 * @param {{hasAdministrations?: boolean}} [options]
 * @returns {boolean}
 */
export const isDoseEditable = (med, { hasAdministrations = false } = {}) => !hasSchedule(med) && !hasAdministrations;

/**
 * The segment in force on `date`.
 *
 * The next segment's start is authoritative for chaining (D3) — boundsPeriod.end
 * is not consulted to close a segment that has a successor, so a chain can carry
 * neither a gap nor an overlap. Only the final segment is bounded, by its own
 * boundsPeriod.end or repeat.count.
 *
 * @param {object} med
 * @param {string|Date} date
 * @param {string} [timeZone] - used only to localize an instant `date` to a calendar day.
 * @returns {object|null}
 */
export const segmentOn = (med, date, timeZone) => {
    const day = toDayKey(date, timeZone);
    if (!day) return null;

    const chain = segmentChain(med);
    let index = -1;
    for (let i = chain.length - 1; i >= 0; i--) {
        if (segmentStart(chain[i]) <= day) {
            index = i;
            break;
        }
    }
    if (index < 0) return null;

    const segment = chain[index];
    if (index < chain.length - 1) return segment;

    const end = segmentEnd(segment);
    if (end && day > end) return null;

    const count = Number(segment?.timing?.repeat?.count);
    if (Number.isFinite(count) && count > 0) {
        const exhausted = countExhaustedOn(segment, count);
        if (exhausted && day > exhausted) return null;
    }
    return segment;
};

// A record retracted as a mistake never expected anything, on any day — the correction has
// to reach the past too. A discontinued one is the opposite: it expected doses right up to
// the day it stopped, which is what keeps a prior day's answer the same after a
// discontinuation.
const VOID_STATUSES = ['entered-in-error', 'cancelled'];

/**
 * The day the medication stops being administered, or null while it runs.
 *
 * @param {object} med
 * @returns {string|null} 'YYYY-MM-DD'
 */
export const administrationEnd = (med) => String(med?.effective?.end || '').slice(0, 10) || null;

/**
 * The moment administration stopped, as facility wall clock, or null when the record states
 * only a day.
 *
 * A discontinuation is immediate: the doses still ahead of it that afternoon are not expected,
 * and reporting them as missed says a nurse failed to give something nobody was going to give.
 * Records written before this stated a bare date and keep meaning the whole of that day - the
 * schedule they described really did run to the end of it.
 *
 * @param {object} med
 * @param {string} [timeZone]
 * @returns {string|null} 'YYYY-MM-DDTHH:mm'
 */
export const administrationEndAt = (med, timeZone) => {
    const end = med?.effective?.end;
    if (!end || String(end).length <= 10) return null;
    try {
        return timezoneApi.getLocalDateTime(end, "yyyy-MM-dd'T'HH:mm", timeZone);
    } catch {
        return null;
    }
};

/**
 * Has this medication been retracted as an error rather than stopped?
 *
 * @param {object} med
 * @returns {boolean}
 */
export const isVoided = (med) => VOID_STATUSES.includes(String(med?.status || '').toLowerCase());

export const EMAR_ROLE = { clinic: 'clinic', self: 'self', none: 'none' };

/**
 * Whether the patient gives this window's doses rather than the clinic.
 *
 * Stated on the window, never on the order: each segment carries its own answer, which is what
 * lets a stretch of home leave be self-administered while the windows either side of it are not.
 * An as-needed order has one window and says it there.
 *
 * @param {object|null} segment
 * @returns {boolean}
 */
export const isSelfAdministered = (segment) => segment?.selfAdministered === true;

/**
 * Where an order's doses are given.
 *
 * The window the schedule names, else the one the order names, else where the patient is: the
 * location of the program they are enrolled in. There is no unassigned state and no account-wide
 * default — one site for a multi-facility account would send every unassigned order in one
 * building to a window in another.
 *
 * The segment is taken from day one and ignored — the schedule-level field is deferred, so adding
 * it later fills in the branch already reserved for it rather than rewriting a resolution.
 *
 * Resolved at read time and never written onto the order: four paths create one, and the two
 * inbound e-prescribing routes know nothing about windows.
 *
 * The other half of this is App\Service\MedPass\AdministrationSite.
 *
 * @param {object} order
 * @param {object|null} segment - the segment in force on the day, from segmentOn()
 * @param {string|null} patientSite - where the patient is enrolled
 * @returns {string|null}
 */
export const administrationSiteFor = (order, segment, patientSite) =>
    // The window first: a schedule moves without the order being rewritten. The order is where
    // a PRN states it, having no schedule to state it on.
    segment?.administeringLocation?.id || order?.administeringLocation?.id || patientSite || null;

/**
 * Whether a medication is the clinic's to give.
 *
 * Three states rather than a boolean: a boolean cannot say that a self-administered medication
 * is in the eMAR but is not a dose. Conflating those two facts is what made a self-administered
 * list impossible without a special case.
 *
 * The terms are ordered and the order is load-bearing:
 *
 *   1.  the order was retracted                     none
 *   1½. (reserved — see below)
 *   2.  no segment is in force on the day           none
 *   3.  the segment is a hold                       none
 *   4.  it is self-administered                     self
 *   5.  otherwise                                   clinic
 *
 * Term 1½ is where a future tracking override goes: a per-medication flag saying the clinic
 * watches this one even though the patient takes it. Naming the position now means adding it
 * later is adding a term rather than hunting for every place that assumed self-administered and
 * absent-from-the-eMAR were the same fact.
 *
 * R-safety: every term added here must widen or hold the set this answers 'clinic' for, never
 * narrow it. A term that narrows it silently takes doses off a nurse's screen.
 *
 * PRN is deliberately not a term. An as-needed order belongs in the eMAR — it is the PRN list —
 * it is simply never due, and due-ness stays in the expectation walk.
 *
 * The other half of this rule is App\Service\MedPass\EmarRole. Same terms, same order, same
 * names, so the two read as translations of one rule.
 *
 * @param {object} med
 * @param {object|null} segment - the segment in force on the day, from segmentOn()
 * @returns {'clinic'|'self'|'none'}
 */
export const emarRoleOf = (med, segment) => {
    if (isVoided(med)) return EMAR_ROLE.none;

    // 1½ — the tracking override belongs on this line.

    // What keeps every e-prescribed home medication out of the eMAR: a carrier with no
    // boundsPeriod.start is not a segment, so nobody ever scheduled it.
    if (!segment) return EMAR_ROLE.none;

    if (isHeld(segment)) return EMAR_ROLE.none;

    if (isSelfAdministered(segment)) return EMAR_ROLE.self;

    return EMAR_ROLE.clinic;
};

/**
 * Has the schedule run out — every segment written, and the last one finished?
 *
 * Distinguished from a schedule that has not begun: a chain whose first segment is still
 * ahead is pending, not complete. Nothing auto-discontinues on this, and nothing prompts;
 * the row says so and the decision stays the clinician's.
 *
 * @param {object} med
 * @param {string|Date} date
 * @param {string} [timeZone]
 * @returns {boolean}
 */
export const isScheduleComplete = (med, date, timeZone) => {
    const chain = segmentChain(med);
    if (!chain.length) return false;
    const day = toDayKey(date, timeZone);
    if (!day) return false;
    if (segmentStart(chain[0]) > day) return false;
    return !segmentOn(med, day, timeZone);
};

/**
 * Build a schedule segment.
 *
 * @param {object} input
 * @param {object} [input.medication] - the MedicationRequest whose carrier supplies the dose.
 * @param {number} [input.sequence=1] - chain position; only a step of 2 or more may carry its own dose.
 * @param {'scheduled'|'prn'|'hold'} [input.type='scheduled']
 * @param {string} input.start - 'YYYY-MM-DD'; what makes the entry a segment rather than the carrier.
 * @param {string[]} [input.timeOfDay]
 * @param {string[]} [input.dayOfWeek] - FHIR codes 'mon'..'sun'
 * @param {number} [input.period=1]
 * @param {string} [input.periodUnit='d']
 * @param {number} [input.administrationWindow] - minutes
 * @param {{type?: 'date'|'count'|'cancellation', date?: string, count?: number}} [input.ends]
 * @param {object} [input.dose] - Quantity, honoured only at sequence 2 or more
 * @param {object} [input.minimumInterval] - Duration; required for PRN
 * @param {number} [input.maxPerDay] - required for PRN
 * @param {object} [input.asNeeded] - CodeableConcept indication for PRN
 * @param {{reason?: object, notes?: string}} [input.hold]
 * @param {object} [input.stepDuration] - the taper step's 'Continue for'. Deliberately not
 *   timing.repeat.boundsDuration: bounds[x] is a choice, and a segment spends it on
 *   boundsPeriod, which is what places the segment in the chain.
 * @param {object} [input.addedBy] - Reference
 * @param {string} [input.addedOn] - ISO instant
 * @param {boolean} [input.selfAdministered] - the patient gives this window's doses, not the clinic.
 * @returns {Dosage}
 */
export const buildSegment = (input = {}) => {
    const {
        medication,
        sequence = 1,
        type = 'scheduled',
        start,
        timeOfDay = [],
        dayOfWeek = [],
        period = 1,
        periodUnit = 'd',
        administrationWindow,
        ends = {},
        dose,
        minimumInterval,
        maxPerDay,
        asNeeded,
        hold,
        stepDuration,
        addedBy,
        addedOn,
        selfAdministered,
        administeringLocation,
    } = input;

    if (!start) throw new Error('buildSegment: a segment requires a start date.');
    if (type === 'prn' && (!minimumInterval || !maxPerDay)) throw new Error('buildSegment: a PRN segment requires both minimumInterval and maxPerDay.');
    if (type === 'scheduled' && WEEK_UNITS.includes(normalizeUnit(periodUnit)) && !dayOfWeek.length) throw new Error('buildSegment: a weekly repeat requires at least one day of week.');

    const repeat = { boundsPeriod: { start } };
    if (ends.type === 'date' && ends.date) repeat.boundsPeriod.end = ends.date;
    // Aidbox types these strictly, and a form field hands back a string.
    if (ends.type === 'count' && Number(ends.count) > 0) repeat.count = Number(ends.count);

    // A PRN or Hold segment carries no timing pattern, so nothing downstream can
    // read it as scheduled. It still carries boundsPeriod — that is what places it
    // in the chain.
    if (type === 'scheduled') {
        repeat.period = Number(period);
        repeat.periodUnit = periodUnit;
        const times = timeOfDay.map(toFhirTime).filter(Boolean);
        if (times.length) repeat.timeOfDay = times;
        if (dayOfWeek.length) repeat.dayOfWeek = [...dayOfWeek];
    }

    const json = { sequence, timing: { repeat } };
    if (type === 'scheduled' && Number(administrationWindow) > 0) json.timing.administrationWindow = Number(administrationWindow);
    if (type === 'prn') {
        json.asNeeded = asNeeded ? { CodeableConcept: asNeeded } : { boolean: true };
        json.minimumInterval = minimumInterval;
        json.maxPerDay = Number(maxPerDay);
    }
    if (type === 'hold') json.hold = { reason: hold?.reason, notes: hold?.notes };

    const segmentDose = sequence >= 2 && dose ? dose : doseQuantityOf(carrierOf(medication));
    if (segmentDose) json.doseAndRate = [{ dose: { Quantity: segmentDose } }];

    // bounds[x] is a choice and boundsPeriod is what places the segment in the chain, so the
    // step's own duration cannot live beside it — Aidbox rejects the pair outright.
    if (stepDuration) json.stepDuration = stepDuration;

    if (addedBy) json.addedBy = addedBy;
    json.addedOn = addedOn || new Date().toISOString();
    if (selfAdministered === true) json.selfAdministered = true;
    // Only a schedule names one: a hold places no dose, and a PRN states it on the order, having
    // no schedule to state it on. Enforced here rather than at each caller, which is where the
    // two disagreed. Absent rather than null - an unnamed site is the window saying nothing,
    // which is what sends administrationSiteFor on to the order and then to where the patient is.
    if (type === 'scheduled' && administeringLocation?.id) json.administeringLocation = administeringLocation;

    return new Dosage(json);
};

const advanceByDuration = (day, duration) => {
    const value = Number(duration?.value);
    if (!Number.isFinite(value) || value <= 0) return null;
    const unit = normalizeUnit(duration.code || duration.unit);
    const date = parseISO(day);
    if (WEEK_UNITS.includes(unit)) return format(addDays(date, value * 7), 'yyyy-MM-dd');
    if (MONTH_UNITS.includes(unit)) return format(addDays(date, value * 30), 'yyyy-MM-dd');
    return format(addDays(date, value), 'yyyy-MM-dd');
};

/**
 * Re-chain taper steps onto `startDate`.
 *
 * Each step contributes only a duration, so step n starts exactly where step
 * n-1 ends: a gap or an overlap is unrepresentable rather than merely invalid.
 * Changing the start date shifts the whole chain and preserves every step's
 * 'Continue for'. The final step may carry no duration — that is what
 * "continue until cancelled" looks like on read (D10).
 *
 * @param {Array<object>} steps - Dosage instances or Dosage-shaped JSON
 * @param {string|Date} startDate
 * @returns {Array<Dosage>}
 */
export const rechainTaper = (steps, startDate) => {
    const list = steps || [];
    let cursor = toDayKey(startDate);
    if (list.length && !cursor) throw new Error('rechainTaper: a taper requires a start date.');

    return list.map((step, index) => {
        const json = typeof step?.toJSON === 'function' ? step.toJSON() : { ...step };
        const repeat = { ...(json.timing?.repeat || {}) };
        const duration = json.stepDuration;
        const nextStart = advanceByDuration(cursor, duration);
        if (!nextStart && index < list.length - 1) throw new Error(`rechainTaper: step ${index + 1} has no duration, so the next step has nowhere to start.`);

        repeat.boundsPeriod = { ...(repeat.boundsPeriod || {}), start: cursor };
        // An inclusive end one day before the successor begins: the chain reads as
        // contiguous in FHIR while the next start stays authoritative (D3).
        if (nextStart) repeat.boundsPeriod.end = format(addDays(parseISO(nextStart), -1), 'yyyy-MM-dd');
        else delete repeat.boundsPeriod.end;

        cursor = nextStart || cursor;
        const rechained = { ...json, sequence: index + 1, timing: { ...(json.timing || {}), repeat } };
        if (duration) rechained.stepDuration = duration;
        return new Dosage(rechained);
    });
};

export default {
    segmentChain,
    administrationEnd,
    administrationEndAt,
    isVoided,
    carrierOf,
    sigTextOf,
    doseQuantityOf,
    isPrn,
    isHeld,
    todayIn,
    nextOccurrences,
    administrationsPerInterval,
    dosesPerDay,
    hasStepDuration,
    timesPerInterval,
    hasSchedule,
    administeredDuplicateOf,
    isScheduleComplete,
    administrationTypeOf,
    administrationTypeColor,
    PRN_COLOR,
    isDoseEditable,
    segmentOn,
    buildSegment,
    rechainTaper,
};
